import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiMiniUser,
  HiBuildingOffice2,
  HiArrowRightOnRectangle,
  HiSquares2X2,
  HiBars3,
  HiXMark,
} from "react-icons/hi2";
import { AuthContext } from "../context/AuthContext";

const CustomIcon = ({ d, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const ContractIcon = (props) => (
  <CustomIcon
    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    {...props}
  />
);

const CreateContractIcon = (props) => (
  <CustomIcon
    d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    {...props}
  />
);

const LogoIcon = (props) => (
  <CustomIcon d="M13 10V3L4 14h7v7l9-11h-7z" {...props} />
);

const Navbar = () => {
  const { authState, logout } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: "/dashboard", name: "Dashboard", icon: HiSquares2X2 },
    { path: "/contracts", name: "Contracts", icon: ContractIcon },
    { path: "/create-contract", name: "Create", icon: CreateContractIcon },
  ];

  const NavLink = ({ to, children, icon: Icon }) => (
    <motion.li whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
      <Link
        to={to}
        className={`flex items-center space-x-2 px-3 py-2 rounded-full transition duration-300 ${
          isActive(to)
            ? "bg-indigo-600 text-white font-semibold shadow-lg"
            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        }`}
        onClick={() => setIsOpen(false)}
      >
        <Icon className="h-5 w-5" />
        <span className="text-sm">{children}</span>
      </Link>
    </motion.li>
  );

  return (
    <nav className="fixed top-0 w-full bg-white dark:bg-gray-900 shadow-lg z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <LogoIcon className="h-8 w-8 text-indigo-600" />
            <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              intelleX CLMS
            </span>
          </Link>

          <div className="hidden md:flex md:items-center md:space-x-2 lg:space-x-4">
            {authState.isAuthenticated && (
              <ul className="flex space-x-1 lg:space-x-2">
                {navItems.map((item) => (
                  <NavLink key={item.path} to={item.path} icon={item.icon}>
                    {item.name}
                  </NavLink>
                ))}
              </ul>
            )}

            <div className="flex items-center space-x-2 lg:space-x-4">
              {authState.isAuthenticated ? (
                <>
                  <motion.div
                    className="hidden lg:flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-full"
                    whileHover={{ scale: 1.05 }}
                  >
                    <HiMiniUser className="h-5 w-5 text-indigo-600" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {`${authState.user.firstname} ${authState.user.lastname}`}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {authState.user.role}
                      </span>
                    </div>
                  </motion.div>
                  <motion.div
                    className="hidden lg:flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-full"
                    whileHover={{ scale: 1.05 }}
                  >
                    <HiBuildingOffice2 className="h-5 w-5 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {authState.user.name}
                    </span>
                  </motion.div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={logout}
                    className="flex items-center space-x-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-full transition duration-300 shadow-md text-sm"
                  >
                    <HiArrowRightOnRectangle className="h-5 w-5" />
                    <span className="hidden lg:inline">Logout</span>
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-3 py-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition duration-300 shadow-md text-sm"
                  >
                    <Link to="/login" className="flex items-center space-x-1">
                      <HiMiniUser className="h-5 w-5" />
                      <span>Login</span>
                    </Link>
                  </motion.button>
                  {/* <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-3 py-2 rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-300 shadow-md text-sm"
                  >
                    <Link
                      to="/register"
                      className="flex items-center space-x-1"
                    >
                      <HiMiniUser className="h-5 w-5" />
                      <span>Sign Up</span>
                    </Link>
                  </motion.button> */}
                </>
              )}
            </div>
          </div>

          <button
            className="md:hidden text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            onClick={toggleMenu}
          >
            {isOpen ? (
              <HiXMark className="h-6 w-6" />
            ) : (
              <HiBars3 className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white dark:bg-gray-900 shadow-lg"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {authState.isAuthenticated && (
                <ul className="space-y-2">
                  {navItems.map((item) => (
                    <NavLink key={item.path} to={item.path} icon={item.icon}>
                      {item.name}
                    </NavLink>
                  ))}
                </ul>
              )}

              <div className="mt-4 space-y-2">
                {authState.isAuthenticated ? (
                  <>
                    <motion.div
                      className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-full"
                      whileHover={{ scale: 1.05 }}
                    >
                      <HiMiniUser className="h-5 w-5 text-indigo-600" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {`${authState.user.firstname} ${authState.user.lastname}`}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {authState.user.role}
                        </span>
                      </div>
                    </motion.div>
                    <motion.div
                      className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-full"
                      whileHover={{ scale: 1.05 }}
                    >
                      <HiBuildingOffice2 className="h-5 w-5 text-indigo-600" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {authState.user.name}
                      </span>
                    </motion.div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center justify-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-full transition duration-300 shadow-md"
                    >
                      <HiArrowRightOnRectangle className="h-5 w-5" />
                      <span>Logout</span>
                    </motion.button>
                  </>
                ) : (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full px-3 py-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition duration-300 shadow-md"
                    >
                      <Link
                        to="/login"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center justify-center space-x-2"
                      >
                        <HiMiniUser className="h-5 w-5" />
                        <span>Login</span>
                      </Link>
                    </motion.button>
                    {/* <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full px-3 py-2 rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-300 shadow-md"
                    >
                      <Link
                        to="/register"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center justify-center space-x-2"
                      >
                        <HiMiniUser className="h-5 w-5" />
                        <span>Sign Up</span>
                      </Link>
                    </motion.button> */}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
